<script setup lang="ts">
/**
 * Metadata-only Docue Agent attachment chip.
 *
 * Evaluated shadcn-vue Attachment for chips/groups/actions/upload states.
 * Phase 1 stays metadata-only (no previews/thumbnails; images match other
 * types), so this chip follows the existing Docue Agent context-chip pattern
 * instead of adopting image-oriented Attachment media.
 */
import prettyBytes from 'pretty-bytes'

const props = defineProps<{
  filename?: string
  mediaType: string
  removable?: boolean
  size?: number
}>()

defineEmits<{
  remove: []
}>()

const { t } = useI18n()
const { locale } = useIntl()

const typeLabel = computed(() => docueAgentAttachmentTypeLabel(props.mediaType))

/** Received file parts can omit the filename; fall back to the format label. */
const title = computed(() => props.filename || typeLabel.value)

const description = computed(() => {
  if (props.size === undefined)
    return typeLabel.value

  return `${typeLabel.value} · ${prettyBytes(props.size, {
    locale: locale.value,
    maximumFractionDigits: 1,
  })}`
})
</script>

<template>
  <div
    class="
      flex max-w-full items-center gap-2 rounded-lg bg-neutral-100 px-2 py-1.5
      text-sm text-neutral-800
    "
  >
    <UiIcon
      :name="docueAgentAttachmentIcon(mediaType)"
      class="size-4 shrink-0"
    />
    <div class="min-w-0 flex-1">
      <p class="truncate font-medium leading-4">
        {{ title }}
      </p>
      <p class="truncate text-[11px] leading-3 text-neutral-500">
        {{ description }}
      </p>
    </div>
    <UiButton
      v-if="removable"
      type="button"
      size="2xs"
      variant="ghost"
      aspect-square
      icon="docue:x-close"
      class="shrink-0"
      :aria-label="t('docueAgent.attachments.remove', { name: title })"
      @click="$emit('remove')"
    />
  </div>
</template>
